import { Field, FieldProps, FormikProps } from 'formik';
import { FormControlLabel, FormHelperText, Stack, styled } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { PaloPositionColumn, PaloPositionTable, PaloQuantityCell } from '@synergy/components-palo';
import { PoolButton } from '@synergy/components';
import { PoolCheckbox, PoolFormikNumberTextfield, PoolTypography } from '@synergy/components-next';
import { ThemeWrapper } from '@synergy/theme-next';
import { useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/AddCircleOutline';

import { DemandDetail } from '../models/demand-detail';
import { DemandDetailAccountItem } from '../models/demand-detail-account-item';
import { DemandDetailFormValues } from '../models/demand-detail-form-values';
import { DemandDetailItem } from '../models/demand-detail-item';
import { PoolAddressAutocompleteList } from '../../../../common/components/autocomplete-list/address-autocomplete-list/pool-address-autocomplete-list';
import { useSelector } from '../../../../store/use-appstate-selector';

const StyledPoolFormikNumberTextfield = styled(PoolFormikNumberTextfield)(() => ({
    // The box around it is the measure (PaloQuantityCell); the field fills it.
    width: '100%',
    '& > div > input': {
        textAlign: 'right',
    },
}));

const StyledPoolButton = styled(PoolButton)(({ theme }) => ({
    fontSize: '14px',
    color: theme.palette.text.primary,

    '& svg': {
        marginRight: theme.spacing(1.5),
    },
}));

interface DemandsDetailItemTableProps {
    demand: DemandDetail;
    demandItem: DemandDetailItem;
    formikProps: FormikProps<DemandDetailFormValues>;
    handleNoDemandChecked: (demandItemId: number, checked: boolean) => void;
    index: number;
}

export const DemandsDetailItemTable = (props: DemandsDetailItemTableProps) => {
    const intl = useIntl();
    const addresses = useSelector(state => state.account?.addresses ?? []);
    const readonly = useSelector(state => state.demands.detail.readonly);
    const { demand, demandItem, formikProps, index, handleNoDemandChecked } = props;
    const [noDemandChecked, setNoDemandChecked] = useState<boolean>(false);

    useEffect(() => {
        if (formikProps.values.demandAccountItems.filter(item => item.demandItemId === demandItem.id).length > 0) {
            return;
        }

        const newDemandDetailAccountItem: DemandDetailAccountItem = {
            deliveryAddress: null,
            quantity: null,
            demandItemId: props.demandItem.id,
            noDemand: false,
        };

        formikProps.setFieldValue(
            'demandAccountItems',
            [...formikProps.values.demandAccountItems, newDemandDetailAccountItem],
            false
        );

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formikProps.values.demandAccountItems]);

    useEffect(() => {
        const items = formikProps.values.demandAccountItems.filter(item => item.demandItemId === demandItem.id);

        const overallNoDemandStatus = items.every(item => item.noDemand);

        if (items.length > 0 && overallNoDemandStatus) {
            setNoDemandChecked(true);
            handleNoDemandChecked(demandItem.id, true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [demandItem]);

    const handleRemovePosition = (itemIndex: number) => {
        const demandAccountItemsClone = [...formikProps.values.demandAccountItems];
        demandAccountItemsClone.splice(itemIndex, 1);
        formikProps.setFieldValue('demandAccountItems', demandAccountItemsClone, true);
    };

    /** The shared position table's columns; the kinds carry the width (LST-S17). */
    const columns: PaloPositionColumn<DemandDetailAccountItem>[] = [
        {
            key: 'deliveryAddress',
            type: 'name',
            label: `${intl.formatMessage({ id: 'app.demands.detail.address' })} *`,
            render: item => renderAddress(item),
        },
        {
            key: 'quantity',
            type: 'quantity',
            label: <FormattedMessage id="app.demands.detail.quantity" />,
            render: item => renderQuantity(item),
        },
    ];

    const renderAddress = (item: DemandDetailAccountItem) => {
        const itemIndex = formikProps.values.demandAccountItems.indexOf(item);

        return (
            <Field name={`demandAccountItems.${itemIndex}.deliveryAddress`}>
                {({ field, meta, form }: FieldProps) => (
                    <>
                        <PoolAddressAutocompleteList
                            autocompleteType="DeliveryAddress"
                            items={addresses}
                            onSelected={selected => form.setFieldValue(field.name, selected)}
                            readOnly={readonly || noDemandChecked}
                            value={field.value}
                            placeholder={intl.formatMessage({ id: 'app.label.deliveryAddress' })}
                            error={!!meta.error && meta.touched}
                        />
                        {meta.error && meta.touched && (
                            <FormHelperText error={true}>
                                <FormattedMessage id={meta.error} />
                            </FormHelperText>
                        )}
                    </>
                )}
            </Field>
        );
    };

    const renderQuantity = (item: DemandDetailAccountItem) => {
        const itemIndex = formikProps.values.demandAccountItems.indexOf(item);

        return (
            <ThemeWrapper>
                {/* Figure and unit in the two fixed boxes every quantity in
                    PALO uses: as an adornment inside the field the unit made
                    the field as wide as the longest one in the column
                    (LST-S1a, #2215 R-69). */}
                <PaloQuantityCell unit={demandItem.orderUnit}>
                    <StyledPoolFormikNumberTextfield
                        name={`demandAccountItems.${itemIndex}.quantity`}
                        disabled={readonly || noDemandChecked}
                    />
                </PaloQuantityCell>
            </ThemeWrapper>
        );
    };

    const handleAddPosition = (demandItemId: number, formikProps: FormikProps<DemandDetailFormValues>) => {
        const demandItem = demand?.demandItems.find(demandItem => demandItem.id === demandItemId);

        if (!demandItem) {
            throw new Error(`Demand item with id "${demandItemId}" does not exist.`);
        }

        const newDemandDetailAccountItem: DemandDetailAccountItem = {
            id:
                (formikProps.values.demandAccountItems.reduce(
                    (max, item) => ((item.id ?? -1) > (max?.id ?? -1) ? item : max),
                    formikProps.values.demandAccountItems[0]
                )?.id ?? 0) + 1,
            deliveryAddress: null,
            quantity: null,
            demandItemId,
            noDemand: false,
        };

        formikProps.setValues(
            {
                ...formikProps.values,
                demandAccountItems: [...formikProps.values.demandAccountItems, newDemandDetailAccountItem],
            },
            false
        );
    };

    const items = formikProps.values.demandAccountItems.filter(item => item.demandItemId === demandItem.id);

    /*
     * Adding an address and "kein Bedarf" used to be a row of the table with
     * empty cells in the other columns. They are not positions, so they stand
     * under the table where the shared component keeps what one does with it.
     */
    const toolbar = readonly ? undefined : (
        <Stack direction="row" alignItems="center" gap={2}>
            <StyledPoolButton
                value={index}
                onClick={() => handleAddPosition(demandItem.id, formikProps)}
                disabled={noDemandChecked}
            >
                <AddIcon color="primary" />
                <FormattedMessage id="app.demands.detail.addAddress" />
            </StyledPoolButton>
            <FormControlLabel
                control={
                    <PoolCheckbox
                        checked={noDemandChecked}
                        onChange={() => {
                            handleNoDemandChecked(demandItem.id, !noDemandChecked);
                            setNoDemandChecked(!noDemandChecked);
                        }}
                    />
                }
                label={<PoolTypography typography="body1" translationId="app.demands.detail.noDemand" />}
                sx={{ m: 0 }}
            />
        </Stack>
    );

    return (
        <PaloPositionTable<DemandDetailAccountItem>
            items={items}
            columns={columns}
            getRowKey={(item, position) => item.id ?? position}
            readonly={readonly}
            toolbar={toolbar}
            rowActions={item =>
                readonly
                    ? []
                    : [
                          {
                              label: <FormattedMessage id="app.common.delete" />,
                              onClick: () =>
                                  handleRemovePosition(formikProps.values.demandAccountItems.indexOf(item)),
                              disabled: noDemandChecked,
                          },
                      ]
            }
        />
    );
};
